/**
 * The app's screen-reader announcement channel (WCAG 4.1.3 Status Messages).
 *
 * One clipped, permanently present region. `announce()` APPENDS a node
 * carrying the message; nothing here is ever rendered or visible.
 *
 * ── Why everything is assertive ────────────────────────────────────────────
 *
 * There were two regions, polite for confirmations and assertive for failures,
 * which is the textbook split. In practice the failures spoke and the
 * confirmations did not: a polite announcement is DISCARDED if the screen
 * reader is already talking, and a success message arrives in the middle of
 * the re-render its own action caused, which is precisely when the screen
 * reader is busiest. "Saved." interrupting is a small cost; "Saved." never
 * being said is the thing being fixed.
 *
 * ── Why a node is appended rather than text replaced ───────────────────────
 *
 * Setting `textContent` on a region that is already there is the textbook
 * pattern, and it is the one that did not speak at all. Screen readers vary in
 * what counts as a change worth reading, and a rewritten text node is
 * frequently coalesced away. An element APPEARING inside a region marked
 * `aria-relevant="additions"` is unambiguous, and it is what Adobe's react-aria
 * `LiveAnnouncer` does for the same reason. The node is removed a few seconds
 * later so nothing accumulates and nothing is left for a virtual cursor.
 *
 * ── Why this is not a React component ──────────────────────────────────────
 *
 * A live region only announces a change to something ALREADY in the
 * accessibility tree. `MessageBar` is mounted when a message opens and
 * unmounted when it closes, so a region rendered inside it is born holding its
 * text: nothing changed, nothing announced, and the message is merely
 * FINDABLE — which is exactly the "only reads on hover" symptom.
 *
 * Mounting `MessageBar` permanently instead is not an option: tss-react and
 * emotion inject styles in COMPONENT MOUNT ORDER and equal specificity is
 * settled by that order, so mounting a styled component earlier silently
 * changes which rules win elsewhere. This module has no styles and renders
 * nothing, so it takes no part in that.
 *
 * ── Why not markup in `public/index.html` ──────────────────────────────────
 *
 * That template is only applied by a build, and this repo ships prebuilt
 * `<env>_build/` folders whose `index.html` would not have it.
 */

/** Out of sight and out of layout, but still in the accessibility tree. */
const CLIPPED =
  "position:absolute;width:1px;height:1px;padding:0;margin:-1px;" +
  "overflow:hidden;clip:rect(0 0 0 0);clip-path:inset(50%);white-space:nowrap;border:0";

/**
 * How long a spent announcement stays in the page.
 *
 * Long enough that a screen reader queued behind other speech still finds the
 * text when it gets to it, short enough that it is gone before anyone browsing
 * with a virtual cursor could run into it.
 */
const LIFETIME_MS = 4000;

let region = null;

/**
 * Created on first use rather than at import, because a module can be
 * evaluated before `<body>` exists. `src/index.js` calls this at startup, so
 * the region is in the page long before any message — which is the whole point
 * of it.
 *
 * A region already in the page is adopted rather than duplicated.
 */
export const setupLiveRegions = () => {
  if (region || typeof document === "undefined" || !document.body) return region;

  region = document.getElementById("bbh-live-announcer");
  if (region) return region;

  region = document.createElement("div");
  region.id = "bbh-live-announcer";
  region.setAttribute("role", "alert");
  region.setAttribute("aria-live", "assertive");
  // Only ADDITIONS are worth reading: the cleanup that removes a spent
  // announcement must not be read out as a change of its own.
  region.setAttribute("aria-relevant", "additions");
  region.setAttribute("aria-atomic", "false");
  region.setAttribute("style", CLIPPED);
  document.body.appendChild(region);

  return region;
};

let cleanupTimer = null;

/**
 * Say something. An empty string stops whatever is currently being announced.
 */
export const announce = (text) => {
  const node = setupLiveRegions();
  if (!node) return;

  const words = typeof text === "string" ? text.trim() : "";

  clearTimeout(cleanupTimer);
  // Emptied first so the same message twice running is still an addition, and
  // so two messages in quick succession do not pile up.
  node.replaceChildren();
  if (!words) return;

  /**
   * MUI's `ModalManager` marks EVERY child of `<body>` `aria-hidden="true"`
   * while a Dialog or Drawer is open, excepting only the modal's own nodes
   * (`Modal/ModalManager.js` → `ariaHiddenSiblings`). This region is a body
   * child, so an open drawer would take it out of the accessibility tree and
   * silence every message raised from inside one — which is most of them.
   *
   * Cleared at the moment of announcing. MUI restores by REMOVING the
   * attribute when the modal closes, so clearing it early costs nothing.
   */
  node.removeAttribute("aria-hidden");

  const line = document.createElement("div");
  line.textContent = words;
  node.appendChild(line);

  cleanupTimer = setTimeout(() => node.replaceChildren(), LIFETIME_MS);
};
